import { createContext, useContext, useEffect, useRef, useState, type Dispatch, type SetStateAction, type ReactNode } from 'react'
import { getAuth, GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut, type User } from 'firebase/auth'
import { doc, getFirestore, onSnapshot, setDoc, Timestamp } from 'firebase/firestore'
import { app } from '../../firebase'
import { TRIAL_DURATION_DAYS } from '../billing/constants'

export interface ProfileData {
    fullName: string
    companyName: string
    profession: string
    phone: string
    city: string
}

interface LawAuthContextType {
    user: User | null
    isAdmin: boolean
    plan: string
    profileData: ProfileData
    subscriptionEnd: Timestamp | null
    setProfileData: Dispatch<SetStateAction<ProfileData>>
    showProfileModal: boolean
    setShowProfileModal: Dispatch<SetStateAction<boolean>>
    isSavingProfile: boolean
    handleSaveProfile: (e: React.FormEvent) => Promise<void>
    handleLogin: () => Promise<void>
    handleLogout: () => Promise<void>
    isUserMenuOpen: boolean
    setIsUserMenuOpen: Dispatch<SetStateAction<boolean>>
    isLoadingProfile: boolean
    isAuthLoading: boolean
    startTrial: () => Promise<boolean>
    hasSeenOnboarding: boolean
    markOnboardingSeen: () => Promise<void>
}

const LawAuthContext = createContext<LawAuthContextType | undefined>(undefined)

export function LawAuthProvider({ children }: { children: ReactNode }) {
    const auth = getAuth(app)
    const db = getFirestore(app, 'dxt-law-db')
    const providerRef = useRef(new GoogleAuthProvider())
    const isProfileCompletedRef = useRef(false)

    const [user, setUser] = useState<User | null>(null)
    const [isAdmin, setIsAdmin] = useState(false)
    const [profileData, setProfileData] = useState<ProfileData>({
        fullName: '',
        companyName: '',
        profession: '',
        phone: '',
        city: ''
    })
    const [showProfileModal, setShowProfileModal] = useState(false)
    const [isSavingProfile, setIsSavingProfile] = useState(false)
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
    const [plan, setPlan] = useState<string>('free')
    const [subscriptionEnd, setSubscriptionEnd] = useState<Timestamp | null>(null)
    const [isLoadingProfile, setIsLoadingProfile] = useState(true)
    const [isAuthLoading, setIsAuthLoading] = useState(true)
    const [hasSeenOnboarding, setHasSeenOnboarding] = useState(false)

    // Track snapshot subscription to clean up when auth state changes or component unmounts
    const userSnapshotUnsubscribe = useRef<(() => void) | null>(null)

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser: User | null) => {
            // Cleanup previous snapshot listener if it exists
            if (userSnapshotUnsubscribe.current) {
                userSnapshotUnsubscribe.current()
                userSnapshotUnsubscribe.current = null
            }

            if (currentUser) {
                // SYNC-FIRST: Ensure user exists in Firestore even if unverified
                // This allows Admins to see "Pending" users in the dashboard
                try {
                    const userRef = doc(db, 'users', currentUser.uid)
                    // We perform a one-off set/merge here for critical auth fields
                    const baseUpdates: any = {
                        email: currentUser.email,
                        emailVerified: currentUser.emailVerified,
                        updated_at: new Date().toISOString()
                    }
                    if (currentUser.displayName) baseUpdates.fullName = currentUser.displayName

                    // Use setDoc with merge to ensure doc exists
                    setDoc(userRef, baseUpdates, { merge: true }).catch(err =>
                        console.error('[LawAuth] Failed to sync unverified user', err)
                    )
                } catch (e) {
                    console.error('[LawAuth] Sync error', e)
                }

                // Strict Email Verification Gate
                // Google Auth users are automatically verified (emailVerified = true).
                // Email/Password users are false until they click the link.
                if (!currentUser.emailVerified) {
                    console.log('[LawAuth] User email not verified')
                    // We do NOT signOut(auth) here to allow the 'Resend Email' feature 
                    // in AuthModal to work (which requires a signed-in user).
                    // But we set our local 'user' state to null so the specific App UI 
                    // treats them as logged out.
                    setUser(null)

                    // Clear any other state
                    setProfileData({ fullName: '', companyName: '', profession: '', phone: '', city: '' })
                    setShowProfileModal(false)
                    setIsUserMenuOpen(false)
                    setPlan('free')
                    setIsAdmin(false)
                    isProfileCompletedRef.current = false
                    setIsLoadingProfile(false)
                    setIsAuthLoading(false)
                    setHasSeenOnboarding(false)
                    return
                }

                setUser(currentUser)
                console.log('[LawAuth] Signed in user:', {
                    uid: currentUser.uid,
                    email: currentUser.email,
                    provider: currentUser.providerId,
                })
            } else {
                setUser(null)
                setIsAdmin(false)
                console.log('[LawAuth] No user signed in')
            }

            if (!currentUser) {
                setProfileData({ fullName: '', companyName: '', profession: '', phone: '', city: '' })
                setShowProfileModal(false)
                setIsUserMenuOpen(false)
                setPlan('free')
                isProfileCompletedRef.current = false
                setIsLoadingProfile(false)
                setIsAuthLoading(false)
                setHasSeenOnboarding(false)
                return
            }

            // Admin Check
            let isUserAdmin = false
            try {
                const tokenResult = await currentUser.getIdTokenResult(true)
                const roles = (tokenResult.claims.roles as string[]) || []
                const role = tokenResult.claims.role as string | undefined
                const isAdminClaim = tokenResult.claims.admin === true
                if (roles.includes('admin') || role === 'admin' || isAdminClaim) {
                    setIsAdmin(true)
                    isUserAdmin = true
                } else {
                    setIsAdmin(false)
                }
            } catch (error) {
                console.error('Error checking admin claim', error)
                setIsAdmin(false)
            }

            const userRef = doc(db, 'users', currentUser.uid)

            // Setup new snapshot listener
            userSnapshotUnsubscribe.current = onSnapshot(userRef, async (snap) => {
                try {
                    // Sync basic auth data to Firestore if missing or changed
                    const updates: Record<string, unknown> = {}
                    let shouldUpdate = false

                    if (snap.exists()) {
                        const data = snap.data()
                        setProfileData({
                            fullName: data.fullName || currentUser.displayName || '',
                            companyName: data.companyName || '',
                            profession: data.profession || '',
                            phone: data.phone || '',
                            city: data.city || ''
                        })
                        setPlan(typeof data.plan === 'string' ? data.plan : 'free')
                        setSubscriptionEnd(data.subscriptionEnd instanceof Timestamp ? data.subscriptionEnd : null)
                        setHasSeenOnboarding(!!data.hasSeenOnboarding)

                        // Check if email needs syncing
                        if (currentUser.email && data.email !== currentUser.email) {
                            updates.email = currentUser.email
                            shouldUpdate = true
                        }

                        // Sync Email Verification Status
                        if (data.emailVerified !== currentUser.emailVerified) {
                            updates.emailVerified = currentUser.emailVerified
                            shouldUpdate = true
                        }

                        // Ensure createdAt exists (normalize between createdAt and created_at)
                        if (!data.createdAt && !data.created_at) {
                            updates.createdAt = new Date().toISOString()
                            shouldUpdate = true
                        }

                        // Sync Google Display Name if missing in DB
                        if (!data.fullName && currentUser.displayName) {
                            updates.fullName = currentUser.displayName
                            shouldUpdate = true
                            // Update local state immediately for better UX
                            setProfileData(prev => ({ ...prev, fullName: currentUser.displayName || '' }))
                        }

                        // Only force profile completion for non-admins
                        if (!isUserAdmin && (!data.profession || !data.phone || !data.city) && !isProfileCompletedRef.current) {
                            setShowProfileModal(true)
                        }
                    } else {
                        setPlan('free')
                        setSubscriptionEnd(null)
                        setHasSeenOnboarding(false)
                        // New user doc - initialize with email and createdAt
                        if (currentUser.email) {
                            updates.email = currentUser.email
                            updates.emailVerified = currentUser.emailVerified
                            updates.createdAt = new Date().toISOString()
                            shouldUpdate = true
                        }
                        // Sync Google Display Name if missing in DB
                        if (currentUser.displayName) {
                            updates.fullName = currentUser.displayName
                            shouldUpdate = true
                            // Update local state immediately for better UX
                            setProfileData(prev => ({ ...prev, fullName: currentUser.displayName || '' }))
                        }
                        // Only force profile completion for non-admins
                        if (!isUserAdmin && !isProfileCompletedRef.current) {
                            setShowProfileModal(true)
                        }
                    }

                    if (shouldUpdate) {
                        await setDoc(userRef, updates, { merge: true })
                    }
                } catch (error) {
                    console.error('Error in user profile sync', error)
                } finally {
                    setIsLoadingProfile(false)
                    setIsAuthLoading(false)
                }
            }, (error) => {
                console.error('Error listening to user profile', error)
                setIsLoadingProfile(false)
                setIsAuthLoading(false)
            })
        })

        return () => {
            if (userSnapshotUnsubscribe.current) userSnapshotUnsubscribe.current()
            unsubscribe()
        }
    }, [auth, db])

    const handleLogin = async () => {
        try {
            await signInWithPopup(auth, providerRef.current)
        } catch (error) {
            console.error('Login failed', error)
        }
    }

    const handleLogout = async () => {
        try {
            await signOut(auth)
            setIsUserMenuOpen(false)
            setIsAdmin(false) // FIX: Explicit reset
            setUser(null)
            setProfileData({ fullName: '', companyName: '', profession: '', phone: '', city: '' })
        } catch (error) {
            console.error('Logout failed', error)
        }
    }

    const handleSaveProfile = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!user) return
        if (!profileData.profession || !profileData.phone || !profileData.city) return

        setIsSavingProfile(true)
        try {
            const userRef = doc(db, 'users', user.uid)
            await setDoc(userRef, {
                email: user.email,
                fullName: profileData.fullName,
                companyName: profileData.companyName,
                profession: profileData.profession,
                phone: profileData.phone,
                city: profileData.city,
                updated_at: new Date().toISOString()
            }, { merge: true })

            isProfileCompletedRef.current = true
            setShowProfileModal(false)
        } catch (error) {
            console.error('Error saving profile', error)
        } finally {
            setIsSavingProfile(false)
        }
    }

    const startTrial = async () => {
        if (!user) return false

        // Protection: Don't allow trial activation if user is already premium or in trial
        if (plan === 'premium' || plan === 'trial') {
            console.warn('[LawAuth] startTrial called but user already has plan:', plan)
            return true // Treat as success since they already have access
        }

        try {
            const userRef = doc(db, 'users', user.uid)
            const now = new Date()
            const trialEnd = new Date(now)
            trialEnd.setDate(trialEnd.getDate() + TRIAL_DURATION_DAYS)

            await setDoc(userRef, {
                plan: 'trial',
                subscriptionEnd: Timestamp.fromDate(trialEnd),
                updated_at: new Date().toISOString(),
                hasSeenOnboarding: true // Mark as seen when starting trial
            }, { merge: true })

            // Optimistic update
            setPlan('trial')
            setSubscriptionEnd(Timestamp.fromDate(trialEnd))
            setHasSeenOnboarding(true)
            return true
        } catch (error) {
            console.error('Error starting trial', error)
            return false
        }
    }

    const markOnboardingSeen = async () => {
        if (!user) return
        try {
            const userRef = doc(db, 'users', user.uid)
            await setDoc(userRef, {
                hasSeenOnboarding: true,
                updated_at: new Date().toISOString()
            }, { merge: true })
            setHasSeenOnboarding(true)
        } catch (error) {
            console.error('Error marking onboarding as seen', error)
        }
    }

    const value: LawAuthContextType = {
        user,
        isAdmin,
        profileData,
        subscriptionEnd,
        setProfileData,
        showProfileModal,
        setShowProfileModal,
        isSavingProfile,
        handleSaveProfile,
        handleLogin,
        handleLogout,
        isUserMenuOpen,
        setIsUserMenuOpen,
        plan,
        isLoadingProfile,
        isAuthLoading,
        startTrial,
        hasSeenOnboarding,
        markOnboardingSeen
    }

    return (
        <LawAuthContext.Provider value={value}>
            {children}
        </LawAuthContext.Provider>
    )
}

export function useLawAuth() {
    const context = useContext(LawAuthContext)
    if (context === undefined) {
        throw new Error('useLawAuth must be used within a LawAuthProvider')
    }
    return context
}
